import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as auth from "@/lib/auth";
import { getBilling } from "@/lib/billing";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const next = await auth.getSession();
    setSession(next);
    return next;
  }, []);

  useEffect(() => {
    let live = true;
    const boot = async () => {
      try {
        const previewCallback =
          process.env.REACT_APP_OFFLINE_PREVIEW === "true" &&
          /(?:^#|[&#])access_token=/.test(window.location.hash);

        if (previewCallback) {
          const next = await auth.consumeAuthCallback();
          if (live) {
            setSession(next);
            window.location.hash = "/home";
          }
        } else {
          await refresh();
        }
      } finally {
        if (live) setLoading(false);
      }
    };

    boot();

    const onStorage = () => refresh();
    window.addEventListener("storage", onStorage);

    return () => {
      live = false;
      window.removeEventListener("storage", onStorage);
    };
  }, [refresh]);

  // Keep RevenueCat entitlement identity aligned with the optional Supabase account.
  // Reflection content remains device-first; only the internal account UUID is used.
  useEffect(() => {
    if (loading) return;
    const billing = getBilling();
    const sync = session?.user?.id
      ? billing.identify?.(session.user.id)
      : billing.clearIdentity?.();
    Promise.resolve(sync).catch((e) =>
      console.warn("[billing] account identity sync:", e?.message || e)
    );
  }, [loading, session?.user?.id]);

  // Native passwordless sign-in returns through the app's custom URL scheme.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let handle;
    let lastHandled = "";

    const handleNativeAuthUrl = async (url) => {
      if (!url?.startsWith("app.unanonymous.client://auth/callback") || url === lastHandled) return;
      lastHandled = url;
      try {
        const next = await auth.consumeAuthUrl(url);
        setSession(next);
        window.history.replaceState({}, document.title, "/home");
        window.dispatchEvent(new PopStateEvent("popstate"));
      } catch (e) {
        console.warn("[auth] native callback:", e?.message || e);
        sessionStorage.setItem("un_auth_callback_error", auth.friendlyAuthError(e));
        window.history.replaceState({}, document.title, "/auth/callback");
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
    };

    CapacitorApp.addListener("appUrlOpen", ({ url }) => handleNativeAuthUrl(url))
      .then((listener) => { handle = listener; })
      .catch((e) => console.warn("[auth] appUrlOpen listener:", e?.message || e));

    CapacitorApp.getLaunchUrl()
      .then((result) => handleNativeAuthUrl(result?.url))
      .catch(() => undefined);

    return () => { handle?.remove?.(); };
  }, []);


  // Callback identity must not change when session/loading updates mid-sign-in.
  const consumeAuthCallback = useCallback(async () => {
    const next = await auth.consumeAuthCallback();
    setSession(next);
    return next;
  }, []);

  const value = useMemo(() => ({
    session,
    user: session?.user || null,
    loading,
    refresh,
    sendMagicLink: auth.sendMagicLink,
    verifyEmailOtp: async (args) => { const next = await auth.verifyEmailOtp(args); setSession(next); return next; },
    startOAuth: auth.startOAuth,
    consumeAuthCallback,
    signOut: async () => { await auth.signOut(); setSession(null); },
    deleteMyAccount: async () => { await auth.deleteMyAccount(); setSession(null); },
    googleEnabled: auth.authConfig.googleEnabled,
  }), [session, loading, refresh, consumeAuthCallback]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
};
